import { gql } from '__generated__'

export const GetRosterSearchResults = gql(/* GraphQL */ `
    query GetRosterSearchResults($limit: Int, $page: Int) {
        rostersByRecentlyViewed(limit: $limit, page: $page) {
            data {
                id
                createdByUser {
                    firstName
                    lastName
                    nickname
                }
                rosterName
                company {
                    value
                }
                placements {
                    placementCount
                }
                createdAt
                status
                sharedRecruiters {
                    id
                    recruiter {
                        user {
                            firstName
                            lastName
                            nickname
                            profileImageKey
                        }
                    }
                }
            }
        }
    }
`)

export const GetRostersForDirectory = gql(/* GraphQL */ `
    query RostersByRecentlyViewed(
        $searches: SearchObject
        $limit: Int
        $page: Int
    ) {
        rostersByRecentlyViewed(
            searches: $searches
            limit: $limit
            page: $page
        ) {
            data {
                createdAt
                id
                rosterName
                company {
                    id
                    value
                }
                industries {
                    industry {
                        id
                        value
                    }
                }
                sectors {
                    sector {
                        id
                        value
                    }
                }
                status
                sharedRecruiters {
                    recruiter {
                        id
                        user {
                            firstName
                            lastName
                            nickname
                            profileImageKey
                        }
                    }
                }
            }
            pageInfo {
                itemTotal
                pageNumber
                pageTotal
            }
        }
    }
`)

export const GetRosterProfile = gql(/* GraphQL */ `
    query RosterProfile($id: ID!) {
        roster(id: $id) {
            id
            permissions {
                canEdit
                canDelete
                canView
            }
            rosterName
            rosterType
            mapping {
                id
                isActive
                mappingFilters
                updatedAt
                createdAt
                updatedByUser {
                    id
                    preferredName
                }
                createdByUser {
                    id
                    preferredName
                }
            }
            status
            createdAt
            updatedAt
            startDateLabel
            placements {
                placementCount
                title {
                    value
                    id
                }
            }
            companyOffices {
                companyOffice {
                    officeLocation {
                        id
                        value
                    }
                }
            }
            clientContacts {
                candidate {
                    user {
                        firstName
                        nickname
                        lastName
                    }
                }
            }
            company {
                id
                value
            }
            industries {
                industry {
                    id
                    value
                }
            }
            sectors {
                sector {
                    id
                    value
                }
            }
            createdByUser {
                preferredName
                firstName
                nickname
                lastName
            }
            updatedByUser {
                preferredName
                firstName
                nickname
                lastName
            }
            sharedRecruiters {
                recruiter {
                    id
                    user {
                        firstName
                        lastName
                        nickname
                        profileImageKey
                    }
                }
            }
            candidateProcessStatusGroupSummary {
                statusSummaryGroup {
                    summaryGroup
                    label
                }
                count
            }
            candidateProcessStatusSummary {
                id
                statusLabel
                statusSummaryGroup {
                    id
                    label
                }
                count
            }
            sharedClients {
                clientId
                client {
                    user {
                        firstName
                        lastName
                        profileImageKey
                    }
                }
            }
        }
    }
`)

export const GetRosterPitchNotes = gql(/* GraphQL */ `
    query GetRosterPitchNotes($rosterId: ID!) {
        roster(id: $rosterId) {
            id
            pitchNote {
                note
                updatedAt
                id
                updatedByUser {
                    firstName
                    lastName
                    nickname
                }
                createdByUser {
                    firstName
                    lastName
                    nickname
                }
            }
        }
    }
`)

export const UpdateRosterPitchNote = gql(/* GraphQL */ `
    mutation UpdateRosterPitchNote($rosterId: ID!, $pitchNote: String!) {
        updatePitchNote(rosterId: $rosterId, pitchNote: $pitchNote) {
            success
            message
        }
    }
`)

export const ArchiveRoster = gql(/* GraphQL */ `
    mutation ArchiveRoster($rosterId: ID!) {
        archiveRoster(rosterId: $rosterId) {
            success
            message
        }
    }
`)

export const GetRosterColors = gql(/* GraphQL */ `
    query GetRosterColors {
        rosterColors {
            id
            value
            hexCode
            order
        }
    }
`)

export const GetRosterPermissions = gql(/* GraphQL */ `
    query GetRosterPermissions($id: ID!) {
        roster(id: $id) {
            id
            rosterName
            permissions {
                canView
                canEdit
                canDelete
            }
        }
    }
`)

export const UpdateRosterCandidateRecruiterNote = gql(/* GraphQL */ `
    mutation UpdateRosterCandidateRecruiterNote(
        $rosterCandidateId: ID!
        $note: String
        $isInternalOnly: Boolean!
    ) {
        updateRosterCandidateRecruiterNote(
            rosterCandidateId: $rosterCandidateId
            note: $note
            isInternalOnly: $isInternalOnly
        ) {
            success
            message
        }
    }
`)

export const UpdateRosterCandidateColors = gql(/* GraphQL */ `
    mutation UpdateRosterCandidateColors(
        $rosterCandidateIds: [ID!]
        $rosterColorIds: [ID!]!
        $rosterId: ID
        $candidateIds: [ID!]
    ) {
        updateRosterCandidateColors(
            rosterCandidateIds: $rosterCandidateIds
            rosterColorIds: $rosterColorIds
            rosterId: $rosterId
            candidateIds: $candidateIds
        ) {
            success
            message
        }
    }
`)

export const RemoveAllRosterCandidateColors = gql(/* GraphQL */ `
    mutation RemoveAllRosterCandidateColors(
        $rosterCandidateIds: [ID!]
        $rosterId: ID
        $candidateIds: [ID!]
    ) {
        removeAllRosterCandidateColors(
            rosterCandidateIds: $rosterCandidateIds
            rosterId: $rosterId
            candidateIds: $candidateIds
        ) {
            success
            message
        }
    }
`)

export const AddCandidatesToRoster = gql(/* GraphQL */ `
    mutation AddCandidatesToRoster(
        $rosterId: ID!
        $candidateIds: [ID!]!
        $processStatusId: ID
        $overwriteExistingStatus: Boolean
    ) {
        addCandidatesToRoster(
            rosterId: $rosterId
            candidateIds: $candidateIds
            processStatusId: $processStatusId
            overwriteExistingStatus: $overwriteExistingStatus
        ) {
            success
            message
            data {
                id
                candidateId
            }
        }
    }
`)

export const BlastCandidates = gql(/* GraphQL */ `
    mutation BlastCandidates(
        $subject: String!
        $rosterId: ID!
        $rosterCandidateIds: [ID!]
        $candidateIds: [ID!]
        $contentRaw: String
        $contentHtml: String
        $jobBoardInput: CreateJobBoardInput
        $emailPreferenceOrder: [String!]
    ) {
        blastCandidates(
            subject: $subject
            rosterId: $rosterId
            rosterCandidateIds: $rosterCandidateIds
            candidateIds: $candidateIds
            contentRaw: $contentRaw
            contentHtml: $contentHtml
            jobBoardInput: $jobBoardInput
            emailPreferenceOrder: $emailPreferenceOrder
        ) {
            success
            message
            data {
                id
                firstName
                nickname
                reason
            }
        }
    }
`)

export const SendTestBlast = gql(/* GraphQL */ `
    mutation SendTestBlast(
        $subject: String!
        $contentRaw: String
        $contentHtml: String
    ) {
        sendTestBlast(
            subject: $subject
            contentRaw: $contentRaw
            contentHtml: $contentHtml
        ) {
            success
            message
        }
    }
`)

export const MyRecruiterRosters = gql(/* GraphQL */ `
    query MyRosters(
        $rosterStatuses: [RosterStatus!]!
        $page: Int
        $limit: Int
    ) {
        recruiterRosters(
            rosterStatuses: $rosterStatuses
            page: $page
            limit: $limit
        ) {
            data {
                id
                rosterName
                rosterType
                mapping {
                    id
                    isActive
                    updatedAt
                    createdAt
                    updatedByUser {
                        id
                        preferredName
                    }
                    createdByUser {
                        id
                        preferredName
                    }
                }
                status
                createdAt
                startDateLabel
                placements {
                    placementCount
                    title {
                        value
                        id
                    }
                }
                companyOffices {
                    companyOffice {
                        officeLocation {
                            id
                            value
                        }
                    }
                }
                company {
                    id
                    value
                }
                industries {
                    industry {
                        id
                        value
                    }
                }
                sectors {
                    sector {
                        id
                        value
                    }
                }
                createdByUser {
                    preferredName
                }
                sharedRecruiters {
                    recruiter {
                        id
                        user {
                            profileImageKey
                            preferredName
                        }
                    }
                }
                candidateProcessStatusGroupSummary {
                    statusSummaryGroup {
                        summaryGroup
                        label
                    }
                    count
                }
                sharedClients {
                    clientId
                    client {
                        id
                        user {
                            preferredName
                            profileImageKey
                        }
                    }
                }
            }
            pageInfo {
                itemTotal
                pageNumber
                pageTotal
            }
        }
    }
`)
